import classNames from "classnames";
import { addDays, addMonths, addWeeks, format, parseISO, subDays, subMonths, subWeeks } from "date-fns";
import { FC, memo, MouseEvent, useEffect, useRef, useState } from "react";

import { ReactComponent as ArrowIcon } from "assets/images/common/arrow.svg";

import { ComplianceTrend, TrendMode, TrendPoint } from "entities/assets";
import { AssetsApi, PortfoliosApi } from "services/http";

import TrendCalendar from "./TrendCalendar";

import styles from "./style.module.scss";

const MODES: { key: TrendMode; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

const Y_TICKS = [100, 80, 60, 40, 20, 0];
const VIEW_WIDTH = 920;
const HEIGHT = 220;
const PLOT = { left: 8, right: 912, top: 12, bottom: 196 };

const MOCK_VALUES = [20, 25, 18, 30, 65, 100, 95, 55, 35, 48, 80, 60, 100, 78, 55, 50, 62, 60, 85, 97, 100, 80, 55, 40];
const MOCK_POINTS: TrendPoint[] = MOCK_VALUES.map((value, hour) => ({ label: `${hour}:00`, value }));

const pointY = (value: number) => PLOT.bottom - (value / 100) * (PLOT.bottom - PLOT.top);

const clampY = (y: number) => Math.max(PLOT.top, Math.min(PLOT.bottom, y));

const smoothPath = (pts: { x: number; y: number }[]) => {
  let path = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = clampY(p1.y + (p2.y - p0.y) / 6);
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = clampY(p2.y - (p3.y - p1.y) / 6);
    path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
  }
  return path;
};

const axisText = (label: string) => (/^\d{2}\.\d{2}\.\d{4}$/.test(label) ? label.split(".")[0] : label);

const Plot: FC<{ points: TrendPoint[] }> = ({ points }) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const total = points.length;
  const pointX = (index: number) => PLOT.left + (total <= 1 ? 0 : (index / (total - 1)) * (PLOT.right - PLOT.left));

  const present = points
    .map((point, index) => ({ ...point, index }))
    .filter((point): point is TrendPoint & { index: number; value: number } => point.value !== null);

  if (present.length === 0) {
    return <div className={styles.empty}>Insufficient data for the selected period</div>;
  }

  const coords = present.map((point) => ({
    x: pointX(point.index),
    y: pointY(point.value),
    label: point.label,
    value: point.value,
  }));

  const line = smoothPath(coords);
  const area = `${line} L ${coords[coords.length - 1].x} ${PLOT.bottom} L ${coords[0].x} ${PLOT.bottom} Z`;

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    const element = chartRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * VIEW_WIDTH;
    let nearest = 0;
    for (let i = 1; i < coords.length; i += 1) {
      if (Math.abs(coords[i].x - x) < Math.abs(coords[nearest].x - x)) nearest = i;
    }
    setHovered(nearest);
  };

  const active = hovered === null ? null : coords[hovered];
  const step = total > 16 ? Math.ceil(total / 12) : 1;

  return (
    <div className={styles.plot}>
      <div className={styles.yAxis}>
        {Y_TICKS.map((tick) => (
          <span key={tick} className={styles.yLabel}>
            {tick}%
          </span>
        ))}
      </div>

      <div className={styles.chart} ref={chartRef} onMouseMove={handleMove} onMouseLeave={() => setHovered(null)}>
        <svg className={styles.svg} viewBox={`0 0 ${VIEW_WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img">
          <defs>
            <linearGradient id="compliance-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {Y_TICKS.map((tick) => (
            <line
              key={tick}
              x1={PLOT.left}
              x2={PLOT.right}
              y1={pointY(tick)}
              y2={pointY(tick)}
              className={styles.grid}
            />
          ))}

          <path d={area} fill="url(#compliance-area)" />
          <path d={line} className={styles.stroke} vectorEffect="non-scaling-stroke" />

          {active && (
            <line
              x1={active.x}
              x2={active.x}
              y1={PLOT.top}
              y2={PLOT.bottom}
              className={styles.guide}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {active && (
          <span
            className={styles.dot}
            style={{ left: `${(active.x / VIEW_WIDTH) * 100}%`, top: `${(active.y / HEIGHT) * 100}%` }}
          />
        )}

        {active && (
          <span className={styles.tooltip} style={{ left: `${(active.x / VIEW_WIDTH) * 100}%` }}>
            <span className={styles.tooltipValue}>{active.value}%</span>
            <span className={styles.tooltipTime}>{active.label}</span>
          </span>
        )}

        <div className={styles.xAxis}>
          {points.map((point, index) =>
            index % step === 0 ? (
              <span
                key={`${point.label}-${index}`}
                className={styles.xLabel}
                style={{ left: `${(pointX(index) / VIEW_WIDTH) * 100}%` }}
              >
                {axisText(point.label)}
              </span>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
};

const shiftDate = (iso: string, mode: TrendMode, direction: 1 | -1): string => {
  const base = parseISO(iso);
  if (mode === "week") return format(direction > 0 ? addWeeks(base, 1) : subWeeks(base, 1), "yyyy-MM-dd");
  if (mode === "month") return format(direction > 0 ? addMonths(base, 1) : subMonths(base, 1), "yyyy-MM-dd");
  return format(direction > 0 ? addDays(base, 1) : subDays(base, 1), "yyyy-MM-dd");
};

type PropTypes = { id?: string; type?: "asset" | "portfolio" };

const ComplianceTrendChart: FC<PropTypes> = ({ id, type = "asset" }) => {
  const [mode, setMode] = useState<TrendMode>("day");
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const [trend, setTrend] = useState<ComplianceTrend | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    let active = true;
    (type === "asset" ? AssetsApi.getComplianceTrend : PortfoliosApi.getComplianceScoreTrend)(id, mode, anchor)
      .then(({ data }) => {
        if (active) setTrend(data);
      })
      .catch(() => {
        if (active) setTrend(null);
      });

    return () => {
      active = false;
    };
  }, [id, mode, anchor, type]);

  const changeMode = (next: TrendMode) => {
    setMode(next);
    setAnchor(undefined);
    setCalendarOpen(false);
  };

  const isDynamic = Boolean(id);
  const points = isDynamic ? (trend?.points ?? []) : MOCK_POINTS;
  const navLabel = isDynamic ? (trend?.label ?? "") : "01.01.2026";
  const canForward = isDynamic ? Boolean(trend?.can_go_forward) : true;
  const currentDate = trend?.date;

  return (
    <section className={styles.card}>
      <h3 className={styles.title}>Compliance score trend</h3>

      <div className={styles.controls}>
        <div className={styles.toggle}>
          {MODES.map((item) => (
            <button
              key={item.key}
              type="button"
              className={classNames(styles.toggleButton, { [styles.toggleActive]: item.key === mode })}
              onClick={() => (isDynamic ? changeMode(item.key) : setMode(item.key))}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className={styles.nav}>
          <button
            type="button"
            className={classNames(styles.navButton, styles.navPrev)}
            onClick={() => isDynamic && currentDate && setAnchor(shiftDate(currentDate, mode, -1))}
          >
            <ArrowIcon />
          </button>

          <div className={styles.navLabelWrap}>
            <button
              type="button"
              className={styles.navLabel}
              onClick={() => isDynamic && currentDate && setCalendarOpen((open) => !open)}
            >
              {navLabel}
            </button>

            {isDynamic && calendarOpen && currentDate && (
              <>
                <div className={styles.calBackdrop} onClick={() => setCalendarOpen(false)} />
                <TrendCalendar
                  value={currentDate}
                  onSelect={(date) => {
                    setAnchor(date);
                    setCalendarOpen(false);
                  }}
                />
              </>
            )}
          </div>

          <button
            type="button"
            className={classNames(styles.navButton, !canForward && styles.navButtonDisabled)}
            disabled={isDynamic && !canForward}
            onClick={() => isDynamic && canForward && currentDate && setAnchor(shiftDate(currentDate, mode, 1))}
          >
            <ArrowIcon />
          </button>
        </div>
      </div>

      <Plot points={points} />
    </section>
  );
};

export default memo(ComplianceTrendChart);
